import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import HeaderStatusAvatar from "./HeaderStatusAvatar";

import { UserContext } from "../../UserContext";
import { openPopup } from "../Common/popupUIStore";
import { ButtonUI, TagUI } from "../UI";
import { pressPoint } from "../UI/glass";
import ConfirmDialog from "../Common/ConfirmDialog";
import useNotificationsUnread from "../../hooks/useNotificationsUnread";
import useStudentBalance from "../../hooks/useStudentBalance";
import { formatLei } from "../../api/balanceService";
import {
   runHomeEditAction,
   setHomeEditing,
   toggleHomeEditing,
   useHomeEditing,
} from "../SPanel/Home/homeEditStore";

import studentIcon from "../../assets/svg/graduate.svg";
import notificationIcon from "../../assets/svg/mingcute--notification-line.svg";
import cogIcon from "../../assets/svg/mdi--cog-outline.svg";
import cashIcon from "../../assets/svg/mdi--cash-multiple.svg";

export function compactLei(bani) {
   const n = Number(bani) || 0;
   return n % 100 === 0 ? formatLei(n).replace(",00 lei", " lei") : formatLei(n);
}

/**
 * Soldul elevului în rând (doar pe ecran lat și doar când balanța e pornită pe server).
 * Se schimbă singur când personalul alimentează sau corectează (socket); la apăsare se
 * deschide popup-ul „Balanța” (StudentBalancePopup: soldul și ultimele operații).
 */
function BalanceChip() {
   const balance = useStudentBalance();
   const [flash, setFlash] = useState(false);
   const firstValueRef = useRef(true);

   // o scurtă evidențiere când soldul se schimbă (nu la prima afișare)
   useEffect(() => {
      if (!balance.enabled) return undefined;
      if (firstValueRef.current) {
         firstValueRef.current = false;
         return undefined;
      }
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1600);
      return () => clearTimeout(t);
   }, [balance.balanceBani, balance.enabled]);

   if (!balance.loaded || !balance.enabled) return null;

   return (
      <ButtonUI
         size="lg"
         icon={cashIcon}
         className={"studentMobileTopBar__balance" + (flash ? " is-flash" : "")}
         onClick={() => openPopup("studentBalance")}
         aria-label={`Balanța: ${formatLei(balance.balanceBani)}`}
         title="Balanța ta"
      >
         {compactLei(balance.balanceBani)}
      </ButtonUI>
   );
}

function resolveStudentName(user) {
   const first = String(user?.firstName || "").trim();
   const last = String(user?.lastName || "").trim();
   const fromParts = `${first} ${last}`.trim();
   if (fromParts) return fromParts;

   const fallback = String(user?.name || user?.fullName || "").trim();
   return fallback || "Profil student";
}

/**
 * Rândul de sus al elevului (.pageToolbar, ca pe paginile de admin): soldul (doar pe
 * ecran lat),
 * notificările, „Editează” (pe pagina principală) și Setările (fereastra Setări,
 * unde e și „Editează pagina principală”); la editare — în stânga ce se
 * poate face, în dreapta „Adaugă widget”, „Resetează” și „Gata” (widget-urile,
 * homeEditStore). Pe telefon stă
 * fix sus pe toate paginile elevului, cu elementele de sticlă (AppShell), și are
 * în stânga profilul, la fel ca în meniul de pe calculator (header__profil); pe
 * ecran lat apare doar pe pagina principală, în pagină (`variant="inline"`), fără
 * profil (e deja în meniu).
 */
export default function StudentMobileTopBar({ variant = "fixed", showEdit = false }) {
   const { user } = useContext(UserContext);
   const fullName = useMemo(() => resolveStudentName(user), [user]);
   const { unreadCount } = useNotificationsUnread();
   const editing = useHomeEditing() && showEdit;
   const inline = variant === "inline";
   const [confirmReset, setConfirmReset] = useState(false);

   return (
      <div className={"studentMobileTopBar pageToolbar" + (inline ? " studentMobileTopBar--inline" : "")}>
         <ConfirmDialog
            open={confirmReset}
            title="Resetezi pagina?"
            note="Widget-urile revin la cele de la început, în ordinea și mărimile lor. Ce ai adăugat sau mutat se pierde."
            cancelLabel="Nu"
            confirmLabel="Da, resetează"
            tone="danger"
            onCancel={() => setConfirmReset(false)}
            onConfirm={() => {
               setConfirmReset(false);
               runHomeEditAction("reset");
            }}
         />
         {editing ? (
            // ce se poate face, spus pe față (pe telefon doar titlul)
            <div className="studentMobileTopBar__editHint" role="status">
               <strong>Editezi pagina</strong>
               <span>Trage un widget ca să-l muți · „−” îl scoate · mărimea se schimbă din colț</span>
            </div>
         ) : inline ? null : (
            // ca în meniul de pe calculator (Header.jsx, stilurile din _header.scss)
            <button
               type="button"
               className="header__profil header__profil--button studentMobileTopBar__profile"
               onClick={() => openPopup("studentProfile")}
               onPointerDown={pressPoint}
               aria-label="Deschide profilul"
            >
               <HeaderStatusAvatar person={user} iconSrc={studentIcon} />
               <div className="header__profil-details">
                  <h1 title={fullName}>{fullName}</h1>
                  <p title="Student">Student</p>
               </div>
            </button>
         )}

         <div className="pageToolbar__controls studentMobileTopBar__controls">
            {editing ? (
               <>
                  <ButtonUI
                     size="lg"
                     icon="add"
                     className="studentMobileTopBar__add"
                     aria-label="Adaugă un widget"
                     title="Adaugă un widget"
                     onClick={() => runHomeEditAction("add")}
                  />
                  {/* resetarea întreabă într-o fereastră (Da / Nu), nu pe loc în rând */}
                  <ButtonUI size="lg" title="Pune widget-urile ca la început" onClick={() => setConfirmReset(true)}>
                     Resetează
                  </ButtonUI>
                  <ButtonUI size="lg" variant="primary" icon="check" onClick={() => setHomeEditing(false)}>
                     Gata
                  </ButtonUI>
               </>
            ) : (
               <>
                  {/* soldul: doar pe ecran lat (pe telefon e în widget-ul Balanță) */}
                  {inline && <BalanceChip />}
                  <span className="studentMobileTopBar__slot">
                     <ButtonUI
                        size="lg"
                        icon={notificationIcon}
                        onClick={() => openPopup("notifications")}
                        aria-label={unreadCount > 0 ? `Deschide notificările (${unreadCount} necitite)` : "Deschide notificările"}
                        title="Notificările"
                     />
                     <TagUI corner count={unreadCount} />
                  </span>
                  {/* „Editează” la vedere pe pagina principală (era doar în meniul Setări) */}
                  {showEdit && (
                     <ButtonUI
                        size="lg"
                        icon="edit"
                        className="studentMobileTopBar__edit"
                        onClick={toggleHomeEditing}
                        aria-label="Editează pagina"
                        title="Editează pagina: mută, scoate sau adaugă widget-uri"
                     >
                        {inline ? "Editează" : null}
                     </ButtonUI>
                  )}
                  {/* Setările direct, pe orice ecran; „Editează pagina” e și în fereastră */}
                  <ButtonUI
                     size="lg"
                     icon={cogIcon}
                     onClick={() => openPopup("appearanceSettings")}
                     aria-label="Setări"
                     title="Setări"
                     className="studentMobileTopBar__settings"
                  />
               </>
            )}
         </div>
      </div>
   );
}
